<template>
  <div v-if="$apollo.loading">
    <spinner />
  </div>
  <div v-else style="max-width: 700px">
    <div class="title-screen">
      <span id="screen-header">Event info</span>
    </div>
    <theater-event-info-form
      :vs="$v.form"
      :show-update-button="true"
      :initial-values="form"
      :show-title="false"
      :organization-slug="organizationSlug"
      :categories="allEventCategories"
      :enable-publishing="enablePublishing"
      :publishing-in-progress="publishingInProgress"
      @update="handleUpdate($event)"
    />
    <simple-snackbar
      :open.sync="snackbarOpen"
      :message-prop="snackbarMessage"
      :color="snackbarColor"
    />
  </div>
</template>

<script>
import { maxLength, required } from 'vuelidate/lib/validators'
import { httpUrl } from '@/validators'

import {
  GET_ORGANIZATION_EVENT_TEMPLATE, GET_ORGANIZATION_EVENT_TEMPLATE_AND_LOGGED_USER,
  UPDATE_EVENT_TEMPLATE_AND_PERFORMANCE,
} from '@/graphql/eventTemplate.graphql'
import SimpleSnackbar from '../shared/snackbars/simple-snackbar'
import Spinner from '../shared/spinner'
import TheaterEventInfoForm from './theaterEventInfoForm.vue'
import { submitGuard } from '@/utils'
import { GET_ALL_EVENT_CATEGORIES } from '@/graphql/eventCategory.graphql'
import { omit } from 'lodash'

export default {
  name: 'event-info-container',
  components: {
    SimpleSnackbar,
    Spinner,
    TheaterEventInfoForm,
  },
  props: {
    eventTemplateId: {
      type: String,
      default: null,
    },
    organizationSlug: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      snackbarOpen: false,
      snackbarMessage: '',
      snackbarColor: 'primary',
      form: {},
      allEventCategories: [],
      enablePublishing: false,
      publishingInProgress: false,
    }
  },
  validations() {
    return {
      form: {
        title: { required, maxLength: maxLength(120) },
        genreId: {
          required,
        },
        durationInMins: {
          required,
        },
        instagramLink: {
          httpUrl,
        },
        webLink: {
          httpUrl,
        },
        facebookLink: {
          httpUrl,
        },
      },
    }
  },
  methods: {
    async handleUpdate(publishChanges) {
      this.$v.form.$touch()
      if (this.$v.form.$invalid === true) {
        this.snackbarOpen = true
        this.snackbarMessage = 'Fill in the required information'
        this.snackbarColor = 'error'

        return
      }
      await submitGuard(this, async () => {
        if (publishChanges) {
          this.publishingInProgress = true
        }
        await this.$apollo.mutate({
          mutation: UPDATE_EVENT_TEMPLATE_AND_PERFORMANCE,
          variables: {
            organizationSlug: this.organizationSlug,
            eventTemplateInput: this.form,
            publishChanges,
          },
        })

        this.snackbarOpen = true
        this.snackbarMessage = `Event details saved${publishChanges ? ' and will be live in 10 minutes' : ''}.`
        this.snackbarColor = 'primary'
        this.publishingInProgress = false
      })
    },
  },
  apollo: {
    getOrganizationEventTemplate: {
      query: GET_ORGANIZATION_EVENT_TEMPLATE_AND_LOGGED_USER,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
          eventTemplateId: this.eventTemplateId,
        }
      },
      manual: true,
      result({ data: { getOrganizationEventTemplate, me }, loading }) {
        if (!loading && getOrganizationEventTemplate) {
          this.form = omit(getOrganizationEventTemplate, ['events'])
          this.form.interests =
            [
              ...new Set(
                getOrganizationEventTemplate.events.flatMap(event =>
                  event.interests.map(interest => interest.id),
                ),
              ),
            ] || []

          const { flags } = me
          this.enablePublishing = !!flags.allowPublishEventsToLanding && getOrganizationEventTemplate.events.some(event => event.status === 'PUBLISHED')
        }
      },
    },
    getAllEventCategories: {
      query: GET_ALL_EVENT_CATEGORIES,
      result({ data, loading }) {
        if (!loading && data) {
          this.allEventCategories = data.getAllEventCategories.sort((a, b) => a.id - b.id)
        }
      },
    },
  },
}
</script>

<style scoped></style>
